// ============================================================
// A PLATE'S CALLOUTS: THEIR MEASURES, AND THE PLACEMENT FOR A BODY (plan
// plate-unboxed; it replaced design-gaps P2's wide and tight columns).
//
// The measures - how wide a callout's words reach, the art's contour - are
// here; where everything stands is engine/plate/calloutPlacement's free-spot
// search, which this feeds for a person (its contour, or its region pieces)
// and for a vehicle (its region pieces, with its facing labels).
//
// Pure. Zero React dependency. Frame units (the figure is 2123 x 3000, y down).
// ============================================================

import { calloutName, damageOf, type PlateCallout } from './plateModel';
import { figureBounds, piecePoints, type PlateArt } from './figureArt';
import type { Box, Point } from './frame';
import { PLATE_TYPE, TEXT_BOX } from './plateType';
import {
  cachedOn,
  placeForCell,
  type PlaceCallout,
  type PlaceLabel,
  type Placement,
  type PlacementShape,
} from './calloutPlacement';

/** A plate's frame (its viewBox), in the figure's units. */
export interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A person plate's frame before its art is in (the bench's wide frame); the placement fits the real one. */
export const WIDE_FRAME: Frame = { x: -500, y: -160, w: 3200, h: 3500 };
/** A person plate before its art is in: the bench's frame, nothing placed yet. */
export const PERSON_UNPLACED: CalloutLayout = { frame: WIDE_FRAME, at: {}, labels: {}, k: 1 };

/**
 * Where a person's callout draws its lines about its row (BodyPlate's `Callout` reads them): the name above, the
 * numeral and the tag below. Its band for the placement is these, with the name's cap height and the descents.
 */
export const CALLOUT_DY = { name: -60, numeral: 90, tag: 170 };
/**
 * A callout's band about its row as the text draws it, ascent and descent included (a text's box is about 1.2 of its
 * type): the name's top, the numeral's foot (its 150 px type), the tag's foot. Measured on the machine figure, where
 * the body's numeral touched the left arm's name when the band was the type's alone.
 */
const ABOVE = -CALLOUT_DY.name + 60;
const BELOW = CALLOUT_DY.tag + 15;
const NUMERAL_FOOT = CALLOUT_DY.numeral + 35;

/** What the layout needs of a person's callout: its place and what its lines say. */
export type LayoutCallout = Pick<PlateCallout, 'id' | 'side' | 'row' | 'name' | 'armour' | 'cover' | 'marks'>;

/** Where a plate's callouts and labels stand, the frame round them, and the words' scale (the placement's). */
export type CalloutLayout = Placement;

/** A monospace line's width in frame units: its style's advance (0.6 em) and letter spacing, per character. */
const lineWidth = (text: string, { px, spacing }: { px: number; spacing: number }) =>
  text.length * (px * 0.6 + spacing);

/**
 * How far a callout reaches from its column: its widest line - the short name, the armour and its +cover, the tag
 * (the type sizes are BodyPlate.module.css's; the +cover is drawn smaller, so this errs wide).
 */
export function calloutWidth(c: LayoutCallout): number {
  const numeral = `${c.armour}${c.cover === null ? '' : `+${c.cover}`}`;
  const tag = damageOf(c.marks);
  const { name, numeral: big, tag: small } = PLATE_TYPE;
  return Math.max(lineWidth(calloutName(c.name), name), lineWidth(numeral, big), tag ? lineWidth(tag, small) : 0);
}

const isPoint = (p: unknown): p is Point =>
  Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number';

/** The art's contour as rings of points: one flat ring, or several; anything else is none. */
export function contourRings(art: Pick<PlateArt, 'outline'>): Point[][] {
  const fine = art.outline?.fine;
  if (!Array.isArray(fine)) return [];
  if (fine.length && fine.every(isPoint)) return [fine as Point[]];
  return fine.filter(Array.isArray).map((ring: unknown[]) => ring.filter(isPoint) as Point[]);
}

/** The vehicle studies' frame: the vehicle with its facings round it and the callouts beside (its wide layout). */
export const VEHICLE_FRAME: Frame = { x: -1420, y: -560, w: 4963, h: 4400 };
/** A vehicle plate before its art is in: the studies' frame, nothing placed yet. */
export const VEHICLE_UNPLACED: CalloutLayout = { frame: VEHICLE_FRAME, at: {}, labels: {}, k: 1 };
/** What a vehicle's placement needs of a callout: its side, its dot, its name and the tags listed under it. */
export interface VehicleLayoutCallout {
  id: string;
  side: 'L' | 'R';
  anchor: Point;
  name: string;
  tags: readonly { text: string }[];
}

/** A facing's label, as the layout keeps it clear: its place, its anchor, its word and its armour numeral. */
export interface LayoutLabel {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  word: string;
  armour: number;
}

/** A drawing's region polygons ("x,y x,y ..."), as rings - a vehicle has no contour of its own. */
const regionRings = (regions: Readonly<Record<string, readonly string[]>>): Point[][] =>
  Object.values(regions).flatMap((pieces) => pieces.map(piecePoints));

/**
 * The words above a person's figure (PlateWords): the first line's baseline and the step to the next. The last hit
 * stacks down from it at the left; the hover readout takes the first line (and its locked line the second) across
 * the middle; the sights take the row after both, at the right.
 */
export const WORDS_TOP = -60;
export const WORDS_STEP = 76;
/** The sights' row: after the last hit's lines and the readout's two. */
export const sightsRow = (lastHit: number) => Math.max(2, lastHit);
/**
 * The heights the words hold and no callout may sit in: down to the foot of the sights' row, held whether the sights
 * are up or not (locking a place never moves a callout). An ordinary hit (two lines) reserves no more than none; a
 * longer one (glanced, blocked, the target's nature) pushes the callouts down for as long as it is the last hit.
 */
const wordsBand = (lastHit: number) => [-170, WORDS_TOP + sightsRow(lastHit) * WORDS_STEP + 24] as const;
/** The status line under a person's figure. */
const PERSON_STATUS_BAND = [3170, 3300] as const;

/**
 * The tallest word a callout or label draws, in frame units, as the mock measured it (the text's box, about 1.15 of
 * its type): a person's armour numeral (150 px type), a vehicle's name (64 px).
 */
const PERSON_TEXT = PLATE_TYPE.numeral.px * TEXT_BOX;
const VEHICLE_TEXT = PLATE_TYPE.name.px * TEXT_BOX;

/** A person's callout for the search: its dot, its words' reach, the name above its row and the numeral (and tag) below. */
const personItem = (c: LayoutCallout & Pick<PlateCallout, 'anchor'>): PlaceCallout => ({
  id: c.id,
  side: c.side,
  anchor: c.anchor,
  width: calloutWidth(c),
  above: ABOVE,
  below: damageOf(c.marks) ? BELOW : NUMERAL_FOOT,
});

/** Each drawing's shape for the search, made once per art (so its grid is made once too). */
const shapes = new WeakMap<object, PlacementShape>();

/** A person's drawing for the search: its contour, or - without one - its region pieces; its bounds. */
const personShape = (art: Pick<PlateArt, 'outline' | 'box' | 'regions'>): PlacementShape =>
  cachedOn(shapes, art, () => {
    const rings = contourRings(art);
    return { rings: rings.length ? rings : regionRings(art.regions), bounds: figureBounds(art) };
  });

/**
 * A person plate's placement for its cell: each callout in the free spot beside its part, the frame fitted round the
 * figure, the callouts, its words above (`words`: the last hit's lines) and - when it says something - its status
 * line below.
 */
export function personLayout(
  art: Pick<PlateArt, 'outline' | 'box' | 'regions'>,
  callouts: readonly (LayoutCallout & Pick<PlateCallout, 'anchor'>)[],
  { cell, status, words = 0 }: { cell: { w: number; h: number } | null; status: boolean; words?: number },
): CalloutLayout {
  const bands = status ? [wordsBand(words), PERSON_STATUS_BAND] : [wordsBand(words)];
  return placeForCell(personShape(art), callouts.map(personItem), [], { bands, cell, textUnits: PERSON_TEXT });
}

/** A vehicle's words (its hover readout) in a band just above its drawing, its status line just below it. */
const vehicleWords = (box: Box) => [box[1] - 170, box[1] + 10] as const;
export const vehicleStatus = (box: Box) => [box[3] + 60, box[3] + 190] as const;

/** A vehicle's callout for the search: its name above its row, a tag line per tag below. */
const vehicleItem = (c: VehicleLayoutCallout): PlaceCallout => ({
  id: c.id,
  side: c.side,
  anchor: c.anchor,
  width: Math.max(
    lineWidth(calloutName(c.name), PLATE_TYPE.name),
    ...c.tags.map((t) => lineWidth(t.text, PLATE_TYPE.tag)),
  ),
  above: 105,
  below: 70 + 80 * c.tags.length,
});

/** A facing label for the search: the word in 70 px type and the numeral in 130 px, its cap above the baseline. */
const labelItem = (l: LayoutLabel & { id: string }): PlaceLabel => ({
  id: l.id,
  x: l.x,
  y: l.y,
  anchor: l.anchor,
  width: lineWidth(`${l.word} `, PLATE_TYPE.facing) + lineWidth(String(l.armour), PLATE_TYPE.facingArmour),
  above: 100,
  below: 10,
});

/**
 * A vehicle's drawing for the search, made once per art. The hull is the drawing's leftover region (it has no pieces
 * of its own), so all of it counts as drawn: the box joined with every part's pieces, since a part may reach past the
 * box (the walker's arms, 80 units either side). A callout never lands inside the hull between its parts.
 */
function vehicleShape(art: { regions: Readonly<Record<string, readonly string[]>>; box: Box }): PlacementShape {
  return cachedOn(shapes, art, () => ({ rings: [], bounds: figureBounds(art) }));
}

/**
 * A vehicle plate's placement for its cell: each callout in the free spot beside its part, each facing label on its
 * own side as near the hull as the space allows, the frame fitted round them all.
 */
export function vehicleLayout(
  art: { regions: Readonly<Record<string, readonly string[]>>; box: Box },
  callouts: readonly VehicleLayoutCallout[],
  labels: readonly (LayoutLabel & { id: string })[],
  { cell, status }: { cell: { w: number; h: number } | null; status: boolean },
): CalloutLayout {
  const shape = vehicleShape(art);
  const bands = status ? [vehicleWords(art.box), vehicleStatus(art.box)] : [vehicleWords(art.box)];
  return placeForCell(shape, callouts.map(vehicleItem), labels.map(labelItem), {
    bands,
    cell,
    textUnits: VEHICLE_TEXT,
  });
}

/** The readout's widest line at full size, in frame units: "RIGHT ARM · AP 10 +4 COVER · 01-10 · DESTROYED". */
const READOUT_UNITS = 2430;

/**
 * The plate's words' scale (the hover readout and the words above the figure): their widest line held inside the
 * frame, never grown - a fitted frame round a narrow vehicle is far narrower than the bench's.
 */
export const wordsFit = (frame: Frame) => Math.min(1, Math.round(((frame.w - 120) / READOUT_UNITS) * 20) / 20);
